import React, { useState } from 'react'
import { Row, Col, Select, Button, Icon, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'
import './AddCategoryRow.scss'

const { Option, OptGroup } = Select

const AddCategoryRow = ({ 
  riskKey, 
  availableCategories, 
  onAddCategory,
  isVisible,
  activeCategories = []
}) => {
  const { t } = useTranslation()
  const [selectedCategories, setSelectedCategories] = useState([])

  if (!isVisible) return null

  // Calcular todas las combinaciones posibles y verificar si están todas agregadas
  const categoriasGenerales = availableCategories.filter(cat => 
    ['COLABORADOR', 'DIRECTOR', 'PROVEEDOR', 'CLIENTE'].includes(cat)
  )
  const tipoPersona = availableCategories.filter(cat => 
    ['Entity', 'Person'].includes(cat)
  )

  const allCombinations = [
    ...categoriasGenerales,
    ...tipoPersona,
    ...categoriasGenerales.flatMap(cat => tipoPersona.map(tipo => `${cat}-${tipo}`))
  ]

  // Si todas las combinaciones están agregadas, no mostrar el componente
  if (allCombinations.every(combination => activeCategories.includes(combination))) {
    return null
  }

  const handleAddCategories = () => {
    if (selectedCategories.length > 0) {
      const combinations = generateCategoryCombinations(selectedCategories)
      const validCombinations = []
      const duplicateCombinations = []
      
      combinations.forEach(combination => {
        if (activeCategories.includes(combination)) {
          duplicateCombinations.push(combination)
        } else {
          validCombinations.push(combination)
        }
      })
      
      // Mostrar mensaje de error si hay duplicados
      if (duplicateCombinations.length > 0) {
        const translatedDuplicates = duplicateCombinations.map(combination => 
          t('messages.aml.category.' + combination) || combination
        )
        const duplicateText = translatedDuplicates.join(', ')
        message.error(`${t('messages.aml.duplicateCombination') || 'Combinación duplicada'}: ${duplicateText}`)
      }
      
      // Agregar solo las combinaciones válidas
      validCombinations.forEach(combination => {
        onAddCategory(riskKey, combination)
      })
      
      // Limpiar selección solo si se agregaron combinaciones válidas
      if (validCombinations.length > 0) {
        setSelectedCategories([])
      }
    }
  }

  // Función para validar la selección según las reglas
  const validateSelection = (newSelection) => {
    const categoriasGenerales = newSelection.filter(cat => 
      ['COLABORADOR', 'DIRECTOR', 'PROVEEDOR', 'CLIENTE'].includes(cat)
    )
    const tipoPersona = newSelection.filter(cat => 
      ['Entity', 'Person'].includes(cat)
    )

    // Máximo 2 tags
    if (newSelection.length > 2) {
      return false
    }

    // No se pueden seleccionar 2 categorías generales
    if (categoriasGenerales.length > 1) {
      return false
    }

    // No se pueden seleccionar 2 tipos de persona
    if (tipoPersona.length > 1) {
      return false
    }

    return true
  }

  // Función para manejar el cambio de selección con validación
  const handleSelectionChange = (newSelection) => {
    if (validateSelection(newSelection)) {
      setSelectedCategories(newSelection)
    }
  }

  // Función para generar las combinaciones de categorías
  const generateCategoryCombinations = (selectedCats) => {
    const categoriasGenerales = selectedCats.filter(cat => 
      ['COLABORADOR', 'DIRECTOR', 'PROVEEDOR', 'CLIENTE'].includes(cat)
    )
    const tipoPersona = selectedCats.filter(cat => 
      ['Entity', 'Person'].includes(cat)
    )

    const combinations = []

    // Si solo hay categorías generales sin tipo de persona
    if (categoriasGenerales.length > 0 && tipoPersona.length === 0) {
      categoriasGenerales.forEach(categoria => {
        combinations.push(categoria)
      })
    }
    // Si solo hay tipo de persona sin categorías generales
    else if (tipoPersona.length > 0 && categoriasGenerales.length === 0) {
      tipoPersona.forEach(tipo => {
        combinations.push(tipo)
      })
    }
    // Si hay ambas, crear todas las combinaciones posibles
    else if (categoriasGenerales.length > 0 && tipoPersona.length > 0) {
      categoriasGenerales.forEach(categoria => {
        tipoPersona.forEach(tipo => {
          combinations.push(`${categoria}-${tipo}`)
        })
      })
    }

    return combinations
  }

  // Organizar categorías en grupos
  const getCategorizedOptions = () => {
    const categoriasGenerales = availableCategories.filter(cat => 
      ['COLABORADOR', 'DIRECTOR', 'PROVEEDOR', 'CLIENTE'].includes(cat)
    )
    const tipoPersona = availableCategories.filter(cat => 
      ['Entity', 'Person'].includes(cat)
    )

    const options = []

    // Solo agregar OptGroup si hay categorías en ese grupo
    if (categoriasGenerales.length > 0) {
      options.push(
        <OptGroup key="categorias" label={t('messages.aml.categories') || 'Categorías'}>
          {categoriasGenerales.map(category => (
            <Option key={category} value={category}>
              {t('messages.aml.category.' + category) || category}
            </Option>
          ))}
        </OptGroup>
      )
    }

    if (tipoPersona.length > 0) {
      options.push(
        <OptGroup key="tipoPersona" label={t('messages.aml.personType') || 'Tipo de Persona'}>
          {tipoPersona.map(category => (
            <Option key={category} value={category}>
              {t('messages.aml.category.' + category) || category}
            </Option>
          ))}
        </OptGroup>
      )
    }

    return options
  }

  return (
    <Row style={{ display: "flex" }} className="add-category-row">
      <Col xs={6} className="cell-title category-cell">
        <div style={{ paddingLeft: '20px', color: '#666', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
          <Tooltip title={selectedCategories.length === 0 ? t('messages.aml.mustSelectCategory') : t('messages.aml.addCategory')}>
            <Icon 
              type="plus" 
              onClick={handleAddCategories} 
              disabled={selectedCategories.length === 0} 
              style={{ 
                marginRight: '8px', 
                color: 'white', 
                cursor: selectedCategories.length === 0 ? 'not-allowed' : 'pointer',
                backgroundColor: selectedCategories.length === 0 ? '#d9d9d9' : '#52c41a',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: selectedCategories.length === 0 ? '0 2px 4px rgba(217, 217, 217, 0.3)' : '0 2px 4px rgba(82, 196, 26, 0.3)',
                transition: 'all 0.3s ease',
                flexShrink: 0,
                opacity: selectedCategories.length === 0 ? 0.6 : 1
              }} 
              onMouseEnter={(e) => {
                if (selectedCategories.length > 0) {
                  e.target.style.backgroundColor = '#73d13d';
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 4px 8px rgba(82, 196, 26, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategories.length > 0) {
                  e.target.style.backgroundColor = '#52c41a';
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 2px 4px rgba(82, 196, 26, 0.3)';
                }
              }}
            />
          </Tooltip>
          <Select
            mode="multiple"
            size='small'
            placeholder={t('messages.aml.selectCategory') || 'Seleccionar categoría'}
            value={selectedCategories}
            onChange={handleSelectionChange}
            style={{ width: '200px' }}
            allowClear
            maxTagCount={2}
          >
            {getCategorizedOptions()}
          </Select>
        </div>
      </Col>
      <Col xs={18} className="riesgo-cell risk-category-cell">
      </Col>
    </Row>
  )
}

export default AddCategoryRow
